import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNYData } from '../../common/NyDataObject';
import { useSearchParams } from 'react-router-dom';
import DropdownSection from '../../common/DropdownSelectList';
import { usePersistedDataStore } from '../store';
import {useUser} from '../UserContext'
import { getTaxYearOptions } from '../../common/helper_functions';
// based on user's access level, dictate what they can select.
// may want to abstract this authentication to a higher level hook or something in the future..
// for this to be secure we need to validate against the user's accesss level on BE (because they could change disabled state / send reqs programatically)
// I didn't build this with county level access in mind. will need to revisit with a county contract. shouldn't be a huge refactor from this
function MuniSelectionV2({ error, handleClearError, locationParams, updateLocationParams, restrictedYear }) {
  // this component shoudl use the strings from params to set the value of the title
    // with relevant specificity - if there is a village, show the village name, otherwise muni, otherwise county, otherwise State.
  const {user} = useUser();
  const getReportType = usePersistedDataStore((state) => state.reportType);
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize these values from URL params on component mount
  const state = locationParams.state || 'NY';
  const county = locationParams.county || 'All'; // the county code
  const municipality = locationParams.municipality || 'All';
  const village = locationParams.village || '';
  const TaxYear = locationParams.TaxYear || '';
  const municipalityString = locationParams.municipalityString || '';
  const countyString = locationParams.countyString || '';
  const villageString = locationParams.villageString || '';

  // This userAccess Code is old
  const userAccess = user?.userAccess || { level: '', years: [] };
  const isAdmin = userAccess.level === 'admin' || userAccess.level === 'propadmin' || userAccess.level === 'OperationalAdmin';
  const isTrainingUser = userAccess.level === 'Training';
  const userRestricted = !isAdmin && userAccess.level !== 'aventine' && !isTrainingUser;

  // disabled: userRestricted && !county || villageRunTypeRestricted, // Disable dropdown entirely if no county access is defined
  const villageRunTypeRestricted = 
    getReportType === 'settlement' || 
    getReportType === 'assessment' || 
    getReportType === 'data_analysis' || 
    getReportType === 'evidence_comparison' || 
    locationParams.runType === 'court_date' || 
    locationParams.runType === 'single_parcel';

  const runtypeRestricted = getReportType === 'settlement' 
 
  const states = ['NY', 'TX', 'FL', 'CA', 'OH'];
  const { getCounties, getMunicipalities, getVillages } = useNYData();

  const title = useMemo(() => {
    // Depending on the specificity of the selection, show the corresponding title
    if (villageString && villageString !== 'All') {
      return `Village of ${villageString}`;
    } else if (municipalityString && municipalityString !== 'All') {
      return `Town of ${municipalityString}`;
    } else if (countyString && countyString !== 'All') {
      return `${countyString} County`;
    } else if (countyString === 'All') {
      return 'All Counties';
    }
    return state ? `${state} State` : 'New York State';
  }, [villageString, municipalityString, countyString, state]);
  // Get initial values based on userAccess
  const counties = getCounties();
  const countyLookup = useMemo(
    () => Object.fromEntries(counties.map(({ key, selectVal }) => [selectVal, key])),
    [counties]
  );
  
  const municipalities = useMemo(() => {
    if (county) {
      // Use selectedCounty for dynamic lookup
      return getMunicipalities(countyLookup[county]) || [];
    }
    // Fallback to initialCounty if no selection
    return county ? getMunicipalities(countyLookup[county]) : [];
  }, [county, county, getMunicipalities, countyLookup, county]);
  
  const municipalityLookup = useMemo(
    () => Object.fromEntries(municipalities.map(({ key, selectVal }) => [selectVal, key])),
    [municipalities]
  );
  
  const villages = useMemo(() => {
    if (municipality && county) {
      // Use municipality and county for dynamic lookup
      return getVillages(countyLookup[county], municipalityLookup[municipality]) || [];
    }
    // Fallback to initialMunicipality and county if no selection
    return county && municipality
      ? getVillages(countyLookup[county], municipalityLookup[municipality])
      : [];
  }, [
    municipality,
    county,
    getVillages,
    countyLookup,
    municipalityLookup,
  ]);
  const villageLookup = useMemo(
    () => Object.fromEntries(villages.map(({ key, selectVal }) => [selectVal, key])),
    [villages]
  );
  


  // Generate tooltip message for village field
  const villageTooltip = useMemo(() => {
    if (!villageRunTypeRestricted) return null;
    
    if (getReportType === 'assessment') {
      return 'Villages cannot be run with Assessment Analysis';
    }
    if (getReportType === 'data_analysis') {
      return 'Villages cannot be run with Data Analysis';
    }
    if (getReportType === 'evidence_comparison') {
      return 'Villages cannot be run with Evidence Comparison';
    }
    if (locationParams.runType === 'single_parcel') {
      return 'Villages cannot be run with single parcels';
    }
    if (locationParams.runType === 'court_date') {
      return 'Villages cannot be run with courtdates';
    }
    return null;
  }, [villageRunTypeRestricted, getReportType, locationParams.runType]);

  const yearOptions = useMemo(() => getTaxYearOptions(restrictedYear), [restrictedYear]);

  // Consolidate parameter updates into a single function
  const updateParams = useCallback((updates) => {
    const params = new URLSearchParams(window.location.search);
    const finalUpdates = { ...updates };

    // Process all parameter updates at once
    Object.entries(updates).forEach(([key, value]) => {
      switch (key) {
        case 'state':
          params.set('state', value);
          // Clear dependent fields
          ['county', 'countyString', 'municipality', 'municipalityString', 'village', 'villageString', 'MuniCode'].forEach(k => {
            params.delete(k);
            finalUpdates[k] = '';
          });
          break;

        case 'county':
          params.set('county', value);
          params.set('countyString', countyLookup[value] || 'All');
          params.set('MuniCode', value);
          finalUpdates.countyString = countyLookup[value] || 'All';
          finalUpdates.MuniCode = value;
          // Clear dependent fields
          ['municipality', 'municipalityString', 'village', 'villageString'].forEach(k => {
            params.delete(k);
            finalUpdates[k] = '';
          });
          break;

        case 'municipality':
          const countyCode = params.get('county') || '';
          const newMuniCode = value !== 'All' ? countyCode + value : countyCode;
          params.set('municipality', value);
          params.set('municipalityString', municipalityLookup[value] || 'All');
          params.set('MuniCode', newMuniCode);
          finalUpdates.municipalityString = municipalityLookup[value] || 'All';
          finalUpdates.MuniCode = newMuniCode;
          // Clear dependent fields
          ['village', 'villageString'].forEach(k => {
            params.delete(k);
            finalUpdates[k] = '';
          });
          break;

        case 'village':
          const muniCode = params.get('MuniCode') || '';
          const newVillageMuniCode = value !== 'All' ? muniCode.slice(0, 1) + value : muniCode.slice(0, 3);
          params.set('village', value);
          params.set('villageString', villageLookup[value] || 'All');
          params.set('MuniCode', newVillageMuniCode);
          finalUpdates.villageString = villageLookup[value] || 'All';
          finalUpdates.MuniCode = newVillageMuniCode;
          break;

        case 'TaxYear':
          params.set('TaxYear', value);
          break;

        default:
          params.set(key, value);
      }
    });

    // Single URL update
    setSearchParams(params, { replace: true });
    // Single state update
    updateLocationParams(finalUpdates);
  }, [setSearchParams, updateLocationParams, countyLookup, municipalityLookup, villageLookup]);

  // Replace handleDropdownChange with direct usage of updateParams
  const handleDropdownChange = useCallback((key, value) => {
    // Clear any existing error for this field
    if (error?.[key]) {
      handleClearError(key);
    }
    updateParams({ [key]: value });
  }, [updateParams, error, handleClearError]);

  const dropdowns = useMemo(() => [
    // State
    {
      key: 'state',
      label: 'State',
      options: states.map((state) => ({
        key: state,
        value: state,
        displayText: state,
        // disabled: userRestricted && state !== state, // Restrict selection only if user is restricted
        disabled: userRestricted && state !== state, // Restrict selection only if user is restricted
      })),
      disabled: true, // disable this dropdown until we actually want to use it
      value: state,
      width: 60,
    },
    // County
    {
      key: 'county',
      label: 'County',
      options: counties.map(({ key, selectVal, nameWithSelectVal }) => ({
        key,
        value: selectVal,
        displayText: nameWithSelectVal,
        disabled: userRestricted && selectVal !== county, // Restrict selection only if user is restricted
      })),
      value: county,
      disabled: userRestricted && !county || runtypeRestricted, // Disable dropdown entirely if no county access is defined
      // error state here, if value exists, error = true.
      error: error?.county,
      width: 130,
    },
    // Municipality
    {
      key: 'municipality',
      label: 'Municipality',
      options: municipalities.map(({ key, selectVal, nameWithSelectVal }) => ({
        key,
        value: selectVal,
        displayText: nameWithSelectVal,
        disabled: userRestricted && selectVal !== municipality, // Restrict selection only if user is restricted
      })),
      value: municipality,
      error: error?.municipality,
      disabled: (userRestricted || municipalities.length===0) && (!county || county === 'All' || municipalities.length === 0),
      width: 130,
    },
    // Village
    {
      key: 'village',
      label: 'Village',
      options: villages.map(({ key, selectVal, nameWithSelectVal }) => ({
        key,
        value: selectVal,
        displayText: nameWithSelectVal,
        disabled: villageRunTypeRestricted, // Disable all village options when restricted
      })),
      value: village,
      // error: error?.village,
      disabled: villageRunTypeRestricted || ((userRestricted || villages.length === 0) && (!municipality || villages.length === 0)), // Restrict selection only if user is restricted
      tooltip: villageTooltip,
      width: 130,
    },
    // Tax Year
    {
      key: 'TaxYear',
      label: 'Tax Year',
      options: yearOptions,
      value: TaxYear || '',
      error: error?.TaxYear,
      disabled: runtypeRestricted || !!restrictedYear || userRestricted && userAccess?.years?.length === 0,
      width: 110,
    },
  ], [
    states,
    counties,
    municipalities,
    villages,
    error,
    state,
    county,
    municipality,
    village,
    TaxYear,
    userRestricted, // Dependency for restriction flag
    restrictedYear,
    villageRunTypeRestricted,
    runtypeRestricted,
    villageTooltip,
  ]);

  return <DropdownSection dropdowns={dropdowns} onDropdownChange={handleDropdownChange} title={title}/>;
}

export default MuniSelectionV2;